import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { Shield, Loader2, Check, AlertTriangle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function ResetTwoFactorPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [email, setEmail] = useState('');
    const [step, setStep] = useState<'request' | 'sent' | 'confirm' | 'success' | 'error'>('request');
    const [error, setError] = useState('');
    const [resetEmail, setResetEmail] = useState('');

    // If token in URL, auto-confirm reset
    useEffect(() => {
        if (token) {
            setStep('confirm');
            confirmMutation.mutate();
        }
    }, [token]);

    const requestMutation = useMutation({
        mutationFn: () => api.request2FAReset(email),
        onSuccess: () => {
            setStep('sent');
        },
        onError: (err: Error) => {
            setError(err.message);
        },
    });

    const confirmMutation = useMutation({
        mutationFn: () => api.confirm2FAReset(token!),
        onSuccess: (data) => {
            setResetEmail(data.email);
            setStep('success');
        },
        onError: (err: Error) => {
            setError(err.message);
            setStep('error');
        },
    });

    const handleRequestSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (email) {
            setError('');
            requestMutation.mutate();
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 px-4">
            <div className="w-full max-w-md">
                <div className="card p-8">
                    {/* Request Form */}
                    {step === 'request' && (
                        <>
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-warning-100 dark:bg-warning-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Shield className="w-8 h-8 text-warning-600" />
                                </div>
                                <h1 className="text-2xl font-bold mb-2">
                                    {t('twoFactor.reset.title', 'Reset Two-Factor Authentication')}
                                </h1>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    {t('twoFactor.reset.subtitle', 'Enter your email to receive a reset link')}
                                </p>
                            </div>

                            <form onSubmit={handleRequestSubmit} className="space-y-4">
                                <div>
                                    <label className="label">{t('auth.email', 'Email')}</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input"
                                        placeholder="you@example.com"
                                        required
                                    />
                                </div>

                                {error && (
                                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={requestMutation.isPending}
                                    className="btn-primary w-full"
                                >
                                    {requestMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Mail className="w-4 h-4" />
                                            {t('twoFactor.reset.sendLink', 'Send Reset Link')}
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link to="/login" className="text-sm text-neutral-500 hover:text-neutral-700">
                                    {t('twoFactor.backToLogin', '← Back to login')}
                                </Link>
                            </div>
                        </>
                    )}

                    {/* Email Sent */}
                    {step === 'sent' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Mail className="w-8 h-8 text-success-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">
                                {t('twoFactor.reset.checkEmail', 'Check Your Email')}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {t('twoFactor.reset.emailSent', 'If an account with 2FA exists, we\'ve sent a reset link to your email.')}
                            </p>
                            <p className="text-sm text-neutral-500 mb-6">
                                {t('twoFactor.reset.linkExpiry', 'The link will expire in 1 hour.')}
                            </p>
                            <Link to="/login" className="btn-primary inline-flex">
                                {t('twoFactor.backToLogin', '← Back to login')}
                            </Link>
                        </div>
                    )}

                    {/* Confirming */}
                    {step === 'confirm' && (
                        <div className="text-center py-8">
                            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                            <p className="text-neutral-600">
                                {t('twoFactor.reset.confirming', 'Resetting 2FA...')}
                            </p>
                        </div>
                    )}

                    {/* Success */}
                    {step === 'success' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-success-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">
                                {t('twoFactor.reset.success', '2FA Reset Complete!')}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {t('twoFactor.reset.successMessage', 'Your Two-Factor Authentication has been disabled. You can now login with just your password.')}
                            </p>
                            {resetEmail && (
                                <p className="text-sm text-neutral-500 mb-6">
                                    Email: <strong>{resetEmail}</strong>
                                </p>
                            )}
                            <button
                                onClick={() => navigate('/login')}
                                className="btn-primary"
                            >
                                {t('auth.login', 'Sign In')}
                            </button>
                        </div>
                    )}

                    {/* Error */}
                    {step === 'error' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-danger-100 dark:bg-danger-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-8 h-8 text-danger-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2 text-danger-600">
                                {t('twoFactor.reset.failed', 'Reset Failed')}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {error || t('twoFactor.reset.invalidLink', 'This reset link is invalid or has expired.')}
                            </p>
                            <button
                                onClick={() => {
                                    setStep('request');
                                    setError('');
                                }}
                                className="btn-primary"
                            >
                                {t('twoFactor.reset.tryAgain', 'Try Again')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
